import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Page, PageHeader } from "@/components/Bits";
import { toast } from "sonner";
import { FloppyDisk, Image as ImageIcon, Trash, Buildings, CalendarBlank, ArrowsClockwise, Warning, CheckCircle } from "@phosphor-icons/react";
import { ConnectionsPanel } from "@/pages/Connections";
import { TwoFactorSettings } from "@/components/TwoFactor";
import { ModulesPanel } from "@/components/ModulesPanel";

const TIMEOUT_OPTIONS = [
  { value: 10, label: "10 minutes" },
  { value: 30, label: "30 minutes" },
  { value: 60, label: "60 minutes" },
  { value: 0, label: "Stay signed in" },
];

const CAT_LABEL = { ACCRUAL: "Accrual", PREPAYMENT: "Prepayment", DEFERRED_INCOME: "Deferred Income", DEPRECIATION: "Depreciation" };
const TPL_FREQS = ["monthly", "quarterly", "annual", "weekly", "custom", "full"];
const CAT_BS_KEY = { PREPAYMENT: "prepayment_codes", ACCRUAL: "accrual_codes", DEFERRED_INCOME: "deferred_income_codes", DEPRECIATION: "accumulated_depreciation_codes" };
const EMPTY_TPL = { name: "", category: "PREPAYMENT", frequency: "monthly", num_periods: 12, release_basis: "monthly", source_account_code: "", pl_account_code: "", description: "" };

function TemplatesTab() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_TPL);
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [codeCfg, setCodeCfg] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    api.get("/schedule-templates").then(({ data }) => setTemplates(data.templates || []))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/accounts").then(({ data }) => setAccounts(data.accounts || [])).catch(() => {});
    api.get("/settings").then(({ data }) => setCodeCfg(data.settings || {})).catch(() => {});
  }, []);

  const accName = (code) => accounts.find((a) => a.code === code)?.name || code;
  const bsCandidates = (codeCfg[CAT_BS_KEY[form.category]] || []).map((c) => ({ code: c, name: accName(c) }));

  const remove = async (id) => {
    try { await api.delete(`/schedule-templates/${id}`); setTemplates((t) => t.filter((x) => x.id !== id)); toast.success("Template deleted"); }
    catch { toast.error("Could not delete template"); }
  };

  const create = async () => {
    if (!form.name.trim()) { toast.error("Please enter a template name"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/schedule-templates", { ...form, num_periods: Number(form.num_periods) || null });
      setTemplates((t) => [...t, data.template].sort((a, b) => a.name.localeCompare(b.name)));
      toast.success("Template created");
      setForm(EMPTY_TPL); setShowForm(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create template");
    } finally { setSaving(false); }
  };

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  // clear the balance-sheet code when switching category (codes differ per type)
  const setCategory = (v) => setForm((f) => ({ ...f, category: v, source_account_code: "" }));
  const inCls = "w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-orange focus:ring-2 focus:ring-orange/25 outline-none";

  return (
    <div className="card-surface p-6 max-w-3xl" data-testid="templates-tab">
      <div className="flex items-start justify-between gap-4 mb-1">
        <h3 className="text-navy font-semibold">Schedule templates</h3>
        <button onClick={() => setShowForm((s) => !s)} data-testid="new-template-btn"
          className="shrink-0 flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-orange text-white hover:bg-navy transition-colors">
          {showForm ? "Close" : "New template"}
        </button>
      </div>
      <p className="text-xs text-navy/70 mb-5 max-w-xl">
        Reusable release patterns for this entity. Create one here, or use "Save current as template" in the
        release-schedule window — then apply them with one click to keep treatment consistent.
      </p>

      {showForm && (
        <div className="border border-slate-200 rounded-xl p-4 mb-5 bg-slate-50/60 space-y-3" data-testid="template-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <Lbl label="Name">
              <input data-testid="tpl-name" className={inCls} value={form.name} onChange={(e) => upd("name", e.target.value)}
                placeholder="e.g. Annual insurance — 12 months" />
            </Lbl>
            <Lbl label="Category">
              <select data-testid="tpl-category" className={inCls} value={form.category} onChange={(e) => setCategory(e.target.value)}>
                {Object.entries(CAT_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Lbl>
            <Lbl label="Balance-sheet code (optional)">
              <select data-testid="tpl-bs" className={inCls} value={form.source_account_code} onChange={(e) => upd("source_account_code", e.target.value)}>
                <option value="">— Choose on use —</option>
                {bsCandidates.map((a) => <option key={a.code} value={a.code}>{a.code} — {a.name}</option>)}
              </select>
            </Lbl>
            <Lbl label="Frequency">
              <select data-testid="tpl-frequency" className={inCls} value={form.frequency} onChange={(e) => upd("frequency", e.target.value)}>
                {TPL_FREQS.map((f) => <option key={f} value={f}>{f.charAt(0).toUpperCase() + f.slice(1)}</option>)}
              </select>
            </Lbl>
            <Lbl label="Number of periods">
              <input data-testid="tpl-periods" type="number" min={1} className={inCls} value={form.num_periods} onChange={(e) => upd("num_periods", e.target.value)} />
            </Lbl>
            <Lbl label="Release basis">
              <select data-testid="tpl-basis" className={inCls} value={form.release_basis} onChange={(e) => upd("release_basis", e.target.value)}>
                <option value="monthly">Even periods</option>
                <option value="daily">Daily pro-rata</option>
              </select>
            </Lbl>
            <Lbl label="P&L account code (optional)">
              <input data-testid="tpl-pl" className={inCls} value={form.pl_account_code} onChange={(e) => upd("pl_account_code", e.target.value)} placeholder="e.g. 6010" />
            </Lbl>
            <div className="sm:col-span-2">
              <Lbl label="Default description (optional)">
                <input data-testid="tpl-description" className={inCls} value={form.description} onChange={(e) => upd("description", e.target.value)} placeholder="Journal narration" />
              </Lbl>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={() => { setShowForm(false); setForm(EMPTY_TPL); }} className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-navy hover:bg-navy/5">Cancel</button>
            <button onClick={create} disabled={saving || !form.name.trim()} data-testid="tpl-create-submit"
              className="px-5 py-2 text-sm font-medium rounded-lg bg-orange text-white hover:bg-navy transition-colors disabled:opacity-60">
              {saving ? "CREATING…" : "Create template"}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="font-mono text-navy/65 text-sm tracking-widest py-8">LOADING…</div>
      ) : templates.length === 0 ? (
        <div className="text-sm text-navy/70 py-8 text-center border border-dashed border-slate-200 rounded-lg" data-testid="templates-empty">
          No templates yet for this entity.
        </div>
      ) : (
        <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
          {templates.map((t) => (
            <div key={t.id} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`template-row-${t.id}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium text-navy truncate">{t.name}</div>
                <div className="text-xs text-navy/70 font-mono mt-0.5">
                  {CAT_LABEL[t.category] || t.category} · {t.frequency}{t.num_periods ? ` ×${t.num_periods}` : ""}
                  {t.release_basis === "daily" ? " · daily pro-rata" : ""}
                  {t.source_account_code ? ` · BS ${t.source_account_code}` : ""}{t.pl_account_code ? ` · P&L ${t.pl_account_code}` : ""}
                </div>
              </div>
              <button onClick={() => remove(t.id)} data-testid={`template-delete-${t.id}`}
                className="shrink-0 text-sm px-3 py-1.5 rounded-lg border border-slate-200 text-navy/70 hover:text-negative hover:border-negative transition-colors">
                Delete
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Lbl({ label, children }) {
  return (
    <label className="block">
      <span className="overline text-navy/70 block mb-1.5">{label}</span>
      {children}
    </label>
  );
}

export function SecurityTab() {
  const { user, refresh } = useAuth();
  const [selected, setSelected] = useState(user?.inactivity_timeout ?? 30);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => setSelected(data.user.inactivity_timeout ?? 30)).catch(() => {});
  }, []);

  const choose = async (value) => {
    const prev = selected;
    setSelected(value);
    setBusy(true);
    try {
      await api.put("/auth/inactivity", { inactivity_timeout: value });
      await refresh();
      toast.success(value === 0 ? "You'll stay signed in" : `Auto-lock set to ${value} minutes`);
    } catch (e) {
      setSelected(prev);
      toast.error(e?.response?.data?.detail || "Could not update setting");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="security-tab">
      <TwoFactorSettings />
      <div className="card-surface p-6 max-w-2xl">
      <h3 className="text-navy font-semibold mb-1">Automatic lock on inactivity</h3>
      <p className="text-xs text-navy/70 mb-5 max-w-xl">
        After this period of no activity, your session locks and you'll need to re-enter your password to continue.
        This applies to your login only.
      </p>
      <div className="grid sm:grid-cols-2 gap-3">
        {TIMEOUT_OPTIONS.map((opt) => {
          const active = selected === opt.value;
          return (
            <button key={opt.value} onClick={() => choose(opt.value)} disabled={busy}
              data-testid={`inactivity-option-${opt.value}`}
              className={`flex items-center justify-between rounded-xl border px-4 py-3.5 text-left transition-all duration-150 disabled:opacity-60 ${
                active ? "border-orange bg-orange/[0.06] ring-2 ring-orange/25" : "border-slate-200 hover:border-orange/50 bg-white"
              }`}>
              <span className={`text-sm font-medium ${active ? "text-navy" : "text-navy/70"}`}>{opt.label}</span>
              <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${active ? "border-orange" : "border-slate-300"}`}>
                {active && <span className="w-2 h-2 rounded-full bg-orange" />}
              </span>
            </button>
          );
        })}
      </div>
      <div className="mt-5 text-xs text-navy/65 font-mono" data-testid="inactivity-current">
        {selected === 0 ? "Auto-lock is OFF — you will remain signed in." : `Locking after ${selected} minutes of inactivity.`}
      </div>
      </div>
    </div>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const isAdmin = user?.role === "admin";

  const tabs = [
    { id: "connect", label: "Connect to Xero" },
    { id: "modules", label: "Modules" },
    { id: "codes", label: "Account Codes" },
    { id: "branding", label: "Branding" },
    { id: "templates", label: "Templates" },
    { id: "notifications", label: "Notifications" },
    ...(isAdmin ? [{ id: "controls", label: "Approvals" }] : []),
  ];
  const requested = params.get("tab");
  const [tab, setTab] = useState(
    tabs.some((t) => t.id === requested) ? requested : "codes"
  );

  const selectTab = (id) => {
    setTab(id);
    const next = new URLSearchParams(params);
    next.set("tab", id);
    setParams(next, { replace: true });
  };

  return (
    <Page>
      <PageHeader overline="This organisation" title="Entity Settings"
        subtitle="Configure Xero connection, chart of accounts codes and treatment for the active organisation." />

      <div className="flex items-center gap-1 mb-7 bg-slate-100 p-1 rounded-xl w-fit max-w-full overflow-x-auto">
        {tabs.map((t) => (
          <button key={t.id} onClick={() => selectTab(t.id)} data-testid={`settings-tab-${t.id}`}
            className={`px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition-all duration-150 ${
              tab === t.id ? "bg-white text-navy shadow-card" : "text-slate-600 hover:text-navy"
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "connect" && <ConnectionsPanel />}
      {tab === "modules" && <ModulesTab />}
      {tab === "codes" && <AccountCodesTab />}
      {tab === "branding" && <EntityBrandingTab />}
      {tab === "templates" && <TemplatesTab />}
      {tab === "notifications" && <DigestTab />}
      {tab === "controls" && isAdmin && <PolicyTab />}
    </Page>
  );
}

function ModulesTab() {
  const [history, setHistory] = useState([]);
  const loadHistory = useCallback(() => {
    api.get("/entity/modules/history").then(({ data }) => setHistory(data.history || [])).catch(() => {});
  }, []);
  useEffect(() => {
    loadHistory();
    const h = () => loadHistory();
    window.addEventListener("modules:refresh", h);
    return () => window.removeEventListener("modules:refresh", h);
  }, [loadHistory]);

  const fmt = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };
  // Pull the module name out of the stored detail ("Enabled module(s): X …").
  const moduleFromDetail = (d) => {
    const m = /Enabled modules?(?:\(s\))?:?\s*([^(]+?)\s*\(/i.exec(d || "");
    return m ? m[1].trim() : (d || "");
  };

  // Module keys that have an explicit "switched on" history entry (vs legacy/pre-existing).
  const LABEL_TO_KEY = { "prepayments": "prepayments", "accruals": "accruals", "deferred income": "deferred_income", "fixed assets": "fixed_assets" };
  const historyKeys = Array.from(new Set(history.flatMap((h) => {
    const d = (h.detail || "").toLowerCase();
    return Object.entries(LABEL_TO_KEY).filter(([lbl]) => d.includes(lbl)).map(([, k]) => k);
  })));

  return (
    <div className="max-w-3xl space-y-5" data-testid="modules-tab">
      <div className="card-surface p-6">
        <h3 className="text-navy font-semibold mb-1">Ledger modules</h3>
        <p className="text-xs text-navy/70 mb-5 max-w-xl">
          Switch on only the ledgers this organisation runs. Each module can be turned on at any time,
          but <span className="font-semibold text-navy">never off</span> — a live module posts real journals to Xero.
        </p>
        <ModulesPanel onChange={loadHistory} historyKeys={historyKeys} />
      </div>

      <div className="card-surface p-6" data-testid="modules-history-card">
        <h3 className="text-navy font-semibold mb-1">History</h3>
        <p className="text-xs text-navy/70 mb-4">A permanent record of when each module was switched on for this organisation, and by whom.</p>
        {history.length === 0 ? (
          <div className="text-sm text-navy/65 py-6 text-center border border-dashed border-slate-200 rounded-lg" data-testid="modules-history-empty">
            No modules have been switched on for this organisation yet.
          </div>
        ) : (
          <ol className="relative border-l border-slate-200 ml-1.5 space-y-4" data-testid="modules-history-list">
            {history.map((h, i) => (
              <li key={i} className="ml-5" data-testid={`modules-history-row-${i}`}>
                <span className="absolute -left-[7px] w-3.5 h-3.5 rounded-full bg-positive/20 flex items-center justify-center">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#00A859" }} />
                </span>
                <div className="text-sm text-navy font-medium">Switched on <span className="text-orangeink">{moduleFromDetail(h.detail)}</span></div>
                <div className="text-xs text-navy/60 mt-0.5">{fmt(h.ts)} · {h.user_email || "system"}</div>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

function DigestTab() {
  const [data, setData] = useState(null);
  const [pref, setPref] = useState({ frequency: "weekly", day: 0, hour: 8 });
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/digest/preview").then(({ data }) => {
      setData(data);
      if (data.preference) setPref(data.preference);
    }).catch(() => setData({ entities: [], totals: {} }));
  }, []);

  const save = async (next) => {
    setPref(next);
    setSaving(true);
    try {
      const { data } = await api.put("/digest/preference", next);
      setPref(data);
      toast.success("Digest schedule updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update schedule");
    } finally { setSaving(false); }
  };

  const setFreq = (frequency) => {
    const next = { ...pref, frequency };
    if (frequency === "monthly") next.day = Math.min(Math.max(pref.day || 1, 1), 28);
    else next.day = Math.min(Math.max(pref.day || 0, 0), 6);
    save(next);
  };

  const sendTest = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/digest/send-test");
      if (data.sent) toast.success(`Digest sent to ${data.email}`);
      else toast.message(data.reason || "Nothing to send yet");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send digest");
    } finally { setSending(false); }
  };

  const t = data?.totals || {};
  const stats = [
    { label: "Due next 7 days", value: t.upcoming_count ?? 0, color: "#0E0F3F" },
    { label: "Overdue", value: t.overdue_count ?? 0, color: "#E5484D" },
    { label: "Variances", value: t.variances ?? 0, color: "#F37A24" },
    { label: "Open queries", value: t.open_queries ?? 0, color: "#0E0F3F" },
  ];

  const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const ord = (n) => { const s = ["th", "st", "nd", "rd"], v = n % 100; return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`; };
  const FREQS = [
    { id: "off", label: "Off" },
    { id: "daily", label: "Daily" },
    { id: "weekly", label: "Weekly" },
    { id: "biweekly", label: "Bi-weekly" },
    { id: "monthly", label: "Monthly" },
  ];
  const selCls = "text-sm rounded-lg border border-slate-200 bg-white px-3 py-2 text-navy focus:outline-none focus:ring-2 focus:ring-[#F37A24]/40 disabled:opacity-60";

  const scheduleText = () => {
    if (pref.frequency === "off") return "Off — you won't receive the digest.";
    const time = `${String(pref.hour).padStart(2, "0")}:00`;
    if (pref.frequency === "daily") return `Every day at ${time} (London time).`;
    if (pref.frequency === "biweekly") return `Every other ${DAYS[pref.day]} at ${time} (London time).`;
    if (pref.frequency === "monthly") return `On the ${ord(pref.day)} of each month at ${time} (London time).`;
    return `Every ${DAYS[pref.day]} at ${time} (London time).`;
  };

  return (
    <div data-testid="notifications-tab" className="max-w-2xl space-y-5">
      <div className="card-surface p-6">
        <div className="font-semibold text-navy">Email digest</div>
        <p className="text-sm text-navy/70 mt-1.5">
          A one-glance summary of your book across all connected entities — upcoming releases,
          overdue journals, reconciliation variances and open queries. Choose how often and when to receive it.
        </p>

        <div className="grid sm:grid-cols-3 gap-4 mt-5">
          <label className="flex flex-col gap-1.5">
            <span className="overline text-navy/65">Frequency</span>
            <select data-testid="digest-frequency" className={selCls} disabled={saving}
              value={pref.frequency} onChange={(e) => setFreq(e.target.value)}>
              {FREQS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
            </select>
          </label>

          {(pref.frequency === "weekly" || pref.frequency === "biweekly") && (
            <label className="flex flex-col gap-1.5">
              <span className="overline text-navy/65">Day of week</span>
              <select data-testid="digest-day" className={selCls} disabled={saving}
                value={pref.day} onChange={(e) => save({ ...pref, day: Number(e.target.value) })}>
                {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
              </select>
            </label>
          )}

          {pref.frequency === "monthly" && (
            <label className="flex flex-col gap-1.5">
              <span className="overline text-navy/65">Day of month</span>
              <select data-testid="digest-day" className={selCls} disabled={saving}
                value={pref.day} onChange={(e) => save({ ...pref, day: Number(e.target.value) })}>
                {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{ord(d)}</option>)}
              </select>
            </label>
          )}

          {pref.frequency !== "off" && (
            <label className="flex flex-col gap-1.5">
              <span className="overline text-navy/65">Send at (London)</span>
              <select data-testid="digest-hour" className={selCls} disabled={saving}
                value={pref.hour} onChange={(e) => save({ ...pref, hour: Number(e.target.value) })}>
                {Array.from({ length: 24 }, (_, h) => h).map((h) => (
                  <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>
                ))}
              </select>
            </label>
          )}
        </div>

        <div className={`mt-4 text-xs font-medium px-3 py-2 rounded-lg ${pref.frequency !== "off" ? "bg-positive/10 text-positive" : "bg-navy/[0.04] text-navy/70"}`} data-testid="digest-status">
          {scheduleText()}
        </div>
      </div>

      <div className="card-surface p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="font-semibold text-navy">This week's preview</div>
          <button onClick={sendTest} disabled={sending} data-testid="digest-send-test"
            className="text-sm font-semibold px-4 py-2 rounded-lg text-white transition-opacity hover:opacity-90 disabled:opacity-60"
            style={{ background: "#F37A24" }}>
            {sending ? "Sending…" : "Send me a test"}
          </button>
        </div>
        <div className="grid grid-cols-4 gap-3 mb-5" data-testid="digest-stats">
          {stats.map((s) => (
            <div key={s.label} className="text-center rounded-xl bg-slate-50 py-3">
              <div className="font-heading font-black text-2xl" style={{ color: s.color }}>{s.value}</div>
              <div className="text-[0.7rem] text-navy/70 mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>
        <div className="divide-y divide-slate-100">
          {(data?.entities || []).map((e) => (
            <div key={e.tenant_id} className="flex items-center justify-between py-2.5" data-testid={`digest-entity-${e.tenant_id}`}>
              <span className="text-sm font-medium text-navy">{e.name}</span>
              <span className="text-xs text-navy/70">
                {e.upcoming_count || e.overdue_count || e.variances || e.open_queries
                  ? [e.upcoming_count && `${e.upcoming_count} due`, e.overdue_count && `${e.overdue_count} overdue`, e.variances && `${e.variances} var.`, e.open_queries && `${e.open_queries} quer${e.open_queries === 1 ? "y" : "ies"}`].filter(Boolean).join(" · ")
                  : "All clear"}
              </span>
            </div>
          ))}
          {(data?.entities || []).length === 0 && (
            <div className="text-sm text-navy/65 py-6 text-center" data-testid="digest-empty">Connect a Xero organisation to start receiving digests.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function PolicyTab() {
  const [enabled, setEnabled] = useState(false);
  const [scope, setScope] = useState("all");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      setEnabled(!!data.settings.approval_required);
      setScope(data.settings.approval_scope || "all");
    }).catch(() => {});
  }, []);

  const save = async (nextEnabled, nextScope) => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/approval", { approval_required: nextEnabled, approval_scope: nextScope });
      setEnabled(data.approval_required);
      setScope(data.approval_scope || "all");
      toast.success(`2-person approval ${data.approval_required ? "enabled" : "disabled"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update approval policy");
    } finally { setSaving(false); }
  };
  const toggle = () => save(!enabled, scope);
  const pickScope = (s) => { setScope(s); save(enabled, s); };

  return (
    <div data-testid="controls-tab" className="max-w-2xl">
      <div className="card-surface p-6">
        <div className="flex items-start justify-between gap-6">
          <div>
            <div className="font-semibold text-navy">2-person journal approval</div>
            <p className="text-sm text-navy/70 mt-1.5">
              When enabled, every Manual Journal — daily scheduled releases and new adjustment set-up journals —
              is held in an <b>Approvals</b> queue and must be approved by a different person before it posts to Xero.
            </p>
          </div>
          <button onClick={toggle} disabled={saving} data-testid="approval-toggle" role="switch" aria-checked={enabled}
            className={`relative shrink-0 w-12 h-6.5 rounded-full transition-colors duration-200 ${enabled ? "bg-positive" : "bg-navy/20"} disabled:opacity-60`}
            style={{ height: "26px", width: "48px" }}>
            <span className={`absolute top-[3px] left-[3px] w-5 h-5 rounded-full bg-white shadow transition-transform duration-200 ${enabled ? "translate-x-[22px]" : ""}`} />
          </button>
        </div>
        <div className={`mt-4 text-xs font-medium px-3 py-2 rounded-lg ${enabled ? "bg-positive/10 text-positive" : "bg-navy/[0.04] text-navy/70"}`} data-testid="approval-status">
          {enabled ? "Approval is ON — journals require a second person to approve." : "Approval not enabled — journals post automatically when due."}
        </div>

        {enabled && (
          <div className="mt-5 pt-5 border-t border-slate-100" data-testid="approval-scope">
            <div className="font-semibold text-navy text-sm">Approval scope</div>
            <p className="text-xs text-navy/60 mt-1 mb-3">Choose which posting requires a second person's approval.</p>
            <div className="space-y-2">
              {[["all", "All posting", "Everything, including the automatic daily scheduled releases, is held for approval."],
                ["manual_only", "Manual posting only", "Only manual posts and new set-up journals need approval; the automatic scheduled run posts as usual."]].map(([val, title, desc]) => (
                <label key={val} data-testid={`approval-scope-${val}`}
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${scope === val ? "border-orange bg-orange/[0.04]" : "border-slate-200 hover:border-slate-300"}`}>
                  <input type="radio" name="approval-scope" value={val} checked={scope === val} disabled={saving}
                    onChange={() => pickScope(val)} data-testid={`approval-scope-radio-${val}`} className="accent-orange mt-0.5" />
                  <span>
                    <span className="text-sm font-medium text-navy">{title}</span>
                    <span className="block text-xs text-navy/60 mt-0.5">{desc}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AccountCodesTab() {
  const [accounts, setAccounts] = useState([]);
  const [prepay, setPrepay] = useState(new Set());
  const [accrual, setAccrual] = useState(new Set());
  const [deferred, setDeferred] = useState(new Set());
  const [faCost, setFaCost] = useState(new Set());
  const [accumDep, setAccumDep] = useState(new Set());
  const [depExp, setDepExp] = useState(new Set());
  const [compliance, setCompliance] = useState({});
  const [busy, setBusy] = useState(false);
  const [orgName, setOrgName] = useState("");
  const [activeTid, setActiveTid] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [importFromDate, setImportFromDate] = useState("");
  const [autoSaveState, setAutoSaveState] = useState("idle"); // idle | saving | saved | error
  const hydratedRef = useRef(false);
  const skipAutoSaveRef = useRef(false);

  const loadSuggestions = useCallback(() => {
    api.get("/fixed-assets/code-suggestions").then(({ data }) => setSuggestions(data.suggestions || [])).catch(() => {});
  }, []);

  const [reloadingCoa, setReloadingCoa] = useState(false);
  const reloadCoa = async () => {
    setReloadingCoa(true);
    try {
      const { data } = await api.get("/accounts");
      setAccounts(data.accounts || []);
      loadSuggestions();
      toast.success(`Reloaded ${data.accounts?.length || 0} accounts from Xero`);
    } catch (e) {
      toast.error("Could not reload the chart of accounts from Xero");
    } finally { setReloadingCoa(false); }
  };

  useEffect(() => {
    api.get("/accounts").then(({ data }) => setAccounts(data.accounts)).catch(() => {});
    api.get("/settings").then(({ data }) => {
      skipAutoSaveRef.current = true;
      setPrepay(new Set(data.settings.prepayment_codes));
      setAccrual(new Set(data.settings.accrual_codes));
      setDeferred(new Set(data.settings.deferred_income_codes || []));
      setFaCost(new Set(data.settings.fixed_asset_codes || []));
      setAccumDep(new Set(data.settings.accumulated_depreciation_codes || []));
      setDepExp(new Set(data.settings.depreciation_expense_codes || []));
      setCompliance(data.settings.compliance_tags || {});
      setImportFromDate(data.settings.import_from_date || "");
      setOrgName(data.settings.active_org_name || "");
      setActiveTid(data.settings.active_tenant_id || null);
      hydratedRef.current = true;
    }).catch(() => {});
    loadSuggestions();
  }, [loadSuggestions]);

  const SETTER_BY_KEY = {
    fixed_asset_codes: setFaCost, accumulated_depreciation_codes: setAccumDep, depreciation_expense_codes: setDepExp,
  };
  const acceptSuggestion = (s) => {
    SETTER_BY_KEY[s.cfg_key]?.((prev) => new Set(prev).add(s.code));
    setSuggestions((list) => list.filter((x) => !(x.code === s.code && x.cfg_key === s.cfg_key)));
    toast.info(`Tagged ${s.code} as ${s.role_label.toLowerCase()} — Save to apply`);
  };

  const candidates = accounts.filter((a) => a.class === "ASSET" || a.class === "LIABILITY");
  const assetAccounts = accounts.filter((a) => a.class === "ASSET");
  const expenseAccounts = accounts.filter((a) => a.class === "EXPENSE");

  const toggle = (setter) => (code) =>
    setter((prev) => { const n = new Set(prev); n.has(code) ? n.delete(code) : n.add(code); return n; });

  const setRole = (code, role) => {
    const p = new Set(prepay), a = new Set(accrual), d = new Set(deferred);
    p.delete(code); a.delete(code); d.delete(code);
    if (role === "prepay") p.add(code);
    if (role === "accrual") a.add(code);
    if (role === "deferred") d.add(code);
    setPrepay(p); setAccrual(a); setDeferred(d);
    // Give newly-monitored codes a default UK standard; clear when un-monitored.
    setCompliance((prev) => {
      const next = { ...prev };
      if (role === "none") delete next[code];
      else if (!next[code]) next[code] = "FRS102";
      return next;
    });
  };

  const setStandard = (code, std) =>
    setCompliance((prev) => ({ ...prev, [code]: std }));

  // Build the settings payload, self-healing: never persist codes (or their compliance
  // tags) that aren't in this entity's imported chart — drops orphaned/legacy codes.
  const buildKept = () => {
    const inChart = new Set(accounts.map((a) => a.code));
    const keep = (s) => (accounts.length ? [...s].filter((c) => inChart.has(c)) : [...s]);
    const tags = accounts.length
      ? Object.fromEntries(Object.entries(compliance).filter(([c]) => inChart.has(c)))
      : compliance;
    return {
      prepayment_codes: keep(prepay),
      accrual_codes: keep(accrual),
      deferred_income_codes: keep(deferred),
      fixed_asset_codes: keep(faCost),
      accumulated_depreciation_codes: keep(accumDep),
      depreciation_expense_codes: keep(depExp),
      compliance_tags: tags,
      import_from_date: importFromDate || "",
    };
  };

  const orphanCodes = accounts.length
    ? [...new Set([...prepay, ...accrual, ...deferred, ...faCost, ...accumDep, ...depExp])]
        .filter((c) => !accounts.some((a) => a.code === c))
    : [];

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings", buildKept());
      toast.success("Account codes saved");
      loadSuggestions();
    } finally { setBusy(false); }
  };

  // Auto-save whenever a classification selection changes (debounced). Also self-heals:
  // when codes are found that are no longer in the entity's Xero chart, it persists the
  // pruned list and reflects the cleanup in the UI. Reads pruned values via buildKept().
  const autoSave = async (hadOrphans) => {
    if (!activeTid) return;
    const payload = buildKept();
    setAutoSaveState("saving");
    try {
      await api.put("/settings", payload);
      if (hadOrphans && accounts.length) {
        const removed = orphanCodes.length;
        skipAutoSaveRef.current = true; // reflecting the pruned state must not retrigger a save
        setPrepay(new Set(payload.prepayment_codes));
        setAccrual(new Set(payload.accrual_codes));
        setDeferred(new Set(payload.deferred_income_codes));
        setFaCost(new Set(payload.fixed_asset_codes));
        setAccumDep(new Set(payload.accumulated_depreciation_codes));
        setDepExp(new Set(payload.depreciation_expense_codes));
        setCompliance(payload.compliance_tags);
        if (removed) toast.info(`Removed ${removed} unused code${removed > 1 ? "s" : ""} no longer in ${orgName || "this entity"}'s Xero chart`);
      }
      setAutoSaveState("saved");
      setTimeout(() => setAutoSaveState((s) => (s === "saved" ? "idle" : s)), 2000);
    } catch {
      setAutoSaveState("error");
      toast.error("Auto-save failed — click Save to retry");
    }
  };

  useEffect(() => {
    if (!hydratedRef.current || !activeTid) return;
    const hasOrphans = orphanCodes.length > 0;
    // Skip the very first change caused by hydrating state — unless there are orphans to clean.
    if (skipAutoSaveRef.current && !hasOrphans) { skipAutoSaveRef.current = false; return; }
    skipAutoSaveRef.current = false;
    const t = setTimeout(() => { autoSave(hasOrphans); }, hasOrphans ? 300 : 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prepay, accrual, deferred, faCost, accumDep, depExp, compliance, importFromDate, accounts]);

  const roleOf = (code) =>
    prepay.has(code) ? "prepay" : accrual.has(code) ? "accrual" : deferred.has(code) ? "deferred" : "none";

  return (
    <div data-testid="account-codes-tab">
      <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="font-heading font-bold text-2xl tracking-tight text-navy">Chart of Accounts Codes</h2>
          <p className="text-sm text-navy/70 mt-1 max-w-2xl">Tag the balance-sheet codes the importer should scan for Prepayments and Accruals, and set the UK reporting standard (FRS 102 / FRS 105) each carries.</p>
          {activeTid ? (
            <div className="mt-2 inline-flex items-center gap-2 text-xs bg-orange/10 border border-orange/30 text-orangeink rounded-lg px-3 py-1.5" data-testid="codes-active-entity">
              <Buildings size={14} weight="fill" />
              Configuring codes for <span className="font-semibold">{orgName || "this entity"}</span> — each entity has its own codes.
            </div>
          ) : (
            <div className="mt-2 inline-flex items-center gap-2 text-xs bg-warn/10 border border-warn/40 text-warn rounded-lg px-3 py-1.5" data-testid="codes-no-entity">
              <Buildings size={14} weight="fill" />
              Connect &amp; activate an organisation to configure its codes.
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          {autoSaveState !== "idle" && (
            <span data-testid="autosave-status" className={`inline-flex items-center gap-1.5 text-xs font-medium ${autoSaveState === "error" ? "text-warn" : "text-navy/70"}`}>
              {autoSaveState === "saving" && <><ArrowsClockwise size={14} className="animate-spin" /> Saving…</>}
              {autoSaveState === "saved" && <><CheckCircle size={14} weight="fill" className="text-positive" /> All changes saved</>}
              {autoSaveState === "error" && <><Warning size={14} weight="fill" /> Auto-save failed</>}
            </span>
          )}
          <button onClick={reloadCoa} disabled={reloadingCoa} data-testid="reload-coa-btn"
            className="flex items-center gap-2 border border-slate-200 text-navy px-4 py-2.5 rounded-lg text-sm font-medium hover:border-orange hover:text-orangeink transition-colors duration-200 disabled:opacity-60">
            <ArrowsClockwise size={17} className={reloadingCoa ? "animate-spin" : ""} /> {reloadingCoa ? "RELOADING…" : "Reload from Xero"}
          </button>
          <button onClick={save} disabled={busy} data-testid="save-settings-btn"
            className="flex items-center gap-2 bg-orange text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-navy transition-colors duration-200 disabled:opacity-60">
            <FloppyDisk size={18} /> {busy ? "SAVING…" : "Save"}
          </button>
        </div>
      </div>

      {orphanCodes.length > 0 && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 mb-6 flex items-start justify-between gap-4" data-testid="orphan-codes-banner">
          <div>
            <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm mb-1">
              <Warning size={18} weight="fill" /> {orphanCodes.length} tagged code{orphanCodes.length > 1 ? "s are" : " is"} no longer in this entity&apos;s Xero chart
            </div>
            <p className="text-sm text-amber-900/80">
              These were classified previously but aren&apos;t in {orgName || "this entity"}&apos;s current chart of accounts, so they won&apos;t be used for imports or adjustments:{" "}
              <span className="font-mono font-medium">{orphanCodes.join(", ")}</span>. They&apos;ll be cleaned up automatically — or use “Reload from Xero” if an account was just renamed.
            </p>
          </div>
        </div>
      )}

      <div className="card-surface p-5 mb-6 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="import-from-date-panel">
        <div className="flex-1">
          <label className="text-navy font-semibold text-sm flex items-center gap-2">
            <CalendarBlank size={16} weight="fill" className="text-orangeink" /> Import transactions from
          </label>
          <p className="text-navy/70 text-xs mt-1 max-w-xl">
            Only transactions dated on or after this date are imported (Import, Sync, webhooks &amp; scheduled runs).
            Leave blank to import all history. Use this to skip opening-balance entries before your go-live date.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input type="date" value={importFromDate} data-testid="import-from-date-input"
            onChange={(e) => setImportFromDate(e.target.value)}
            className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-navy outline-none focus:border-orange" />
          {importFromDate && (
            <button onClick={() => setImportFromDate("")} data-testid="import-from-date-clear"
              className="text-xs text-navy/70 hover:text-navy underline">Clear</button>
          )}
        </div>
      </div>

      <div className="card-surface overflow-hidden">
        <div className="grid grid-cols-[1fr_360px_160px] gap-6 px-6 py-3 border-b border-slate-200 bg-slate-50 text-[0.66rem] uppercase tracking-wider font-semibold text-slate-600">
          <span>Account</span><span>Classification</span><span>Compliance Tag</span>
        </div>
        <div className="divide-y divide-slate-100">
          {candidates.map((a) => {
            const role = roleOf(a.code);
            return (
              <div key={a.code} data-testid={`account-row-${a.code}`}
                className="grid grid-cols-[1fr_360px_160px] gap-6 items-center px-6 py-3.5 hover:bg-slate-50">
                <div className="flex items-center gap-3">
                  <span className="font-mono font-medium text-navy tabular w-14">{a.code}</span>
                  <span className="text-sm text-navy/80">{a.name}</span>
                </div>
                <div className="flex border border-slate-200 rounded-lg overflow-hidden text-xs">
                  {[["none", "None"], ["prepay", "Prepayment"], ["accrual", "Accrual"], ["deferred", "Deferred Income"]].map(([v, lbl]) => (
                    <button key={v} data-testid={`role-${a.code}-${v}`} onClick={() => setRole(a.code, v)}
                      className={`px-3 py-1.5 font-medium transition-colors duration-150 border-l border-slate-100 first:border-l-0 ${
                        role === v ? "bg-orange text-white" : "bg-white text-navy/70 hover:bg-navy/5"
                      }`}>
                      {lbl}
                    </button>
                  ))}
                </div>
                <div className="flex justify-start">
                  {role === "none" ? (
                    <span className="text-xs text-slate-300 pl-1" data-testid={`compliance-${a.code}-na`}>—</span>
                  ) : (
                    <div className="flex border border-slate-200 rounded-lg overflow-hidden text-xs" data-testid={`compliance-toggle-${a.code}`}>
                      {["FRS102", "FRS105"].map((std) => (
                        <button key={std} data-testid={`compliance-${a.code}-${std}`} onClick={() => setStandard(a.code, std)}
                          className={`px-3 py-1.5 font-medium transition-colors duration-150 border-l border-slate-100 first:border-l-0 ${
                            (compliance[a.code] || "FRS102") === std ? "bg-navy text-white" : "bg-white text-navy/70 hover:bg-navy/5"
                          }`}>
                          {std === "FRS102" ? "FRS 102" : "FRS 105"}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <p className="text-xs text-navy/65 mt-4 font-mono">
        Expense (P&amp;L) accounts are chosen per item when building a release schedule. Each schedule inherits the standard of its balance-sheet code.
      </p>

      <div className="mt-8" data-testid="fixed-asset-codes">
        <h3 className="font-heading font-bold text-lg text-navy">Fixed Asset & Depreciation codes</h3>
        <p className="text-sm text-navy/70 mt-1 max-w-2xl mb-4">
          Tag the accounts used for depreciation so the Fixed Assets screens use your codes instead of guessing
          from account names. Leave a list empty to fall back to automatic detection.
        </p>

        {suggestions.length > 0 && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50/70 p-4" data-testid="fa-suggestions">
            <div className="text-sm font-semibold text-[#92400E] mb-2">
              {suggestions.length} account{suggestions.length > 1 ? "s" : ""} look like fixed-asset / depreciation accounts but aren't tagged
            </div>
            <div className="flex flex-wrap gap-2">
              {suggestions.map((s) => (
                <button key={`${s.cfg_key}-${s.code}`} onClick={() => acceptSuggestion(s)}
                  data-testid={`fa-suggestion-${s.code}`}
                  className="inline-flex items-center gap-2 text-xs bg-white border border-amber-300 rounded-lg pl-2.5 pr-2 py-1.5 hover:border-orange hover:bg-orange/[0.04] transition-colors">
                  <span className="font-mono font-medium text-navy">{s.code}</span>
                  <span className="text-navy/70 max-w-[180px] truncate">{s.name}</span>
                  <span className="text-[0.6rem] uppercase tracking-wider text-navy/65">{s.role_label}</span>
                  <span className="text-orangeink font-semibold">+ Add</span>
                </button>
              ))}
            </div>
            <p className="text-[0.7rem] text-navy/65 mt-2">Adding tags the account for that role — remember to Save.</p>
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-4">
          <CodePicker label="Fixed-asset cost" testid="fa-cost" accounts={assetAccounts} selected={faCost} onToggle={toggle(setFaCost)} />
          <CodePicker label="Accumulated depreciation" testid="fa-accum" accounts={assetAccounts} selected={accumDep} onToggle={toggle(setAccumDep)} />
          <CodePicker label="Depreciation expense (P&L)" testid="fa-exp" accounts={expenseAccounts} selected={depExp} onToggle={toggle(setDepExp)} />
        </div>
      </div>
    </div>
  );
}

function CodePicker({ label, testid, accounts, selected, onToggle }) {
  return (
    <div className="card-surface overflow-hidden flex flex-col" data-testid={`codepicker-${testid}`}>
      <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50 text-[0.66rem] uppercase tracking-wider font-semibold text-slate-600 flex items-center justify-between">
        <span>{label}</span>
        <span className="text-orangeink">{selected.size || 0}</span>
      </div>
      <div className="max-h-56 overflow-y-auto divide-y divide-slate-100">
        {accounts.length === 0 && <div className="px-4 py-6 text-xs text-navy/65 text-center">No accounts found.</div>}
        {accounts.map((a) => (
          <label key={a.code} data-testid={`${testid}-${a.code}`}
            className="flex items-center gap-2.5 px-4 py-2.5 cursor-pointer hover:bg-slate-50">
            <input type="checkbox" checked={selected.has(a.code)} onChange={() => onToggle(a.code)}
              className="accent-orange w-4 h-4" />
            <span className="font-mono font-medium text-navy text-sm tabular w-12">{a.code}</span>
            <span className="text-sm text-navy/75 truncate">{a.name}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

export function BrandingTab() {
  const [logo, setLogo] = useState(null);
  const [logoBusy, setLogoBusy] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setLogo(data.settings.logo || null)).catch(() => {});
  }, []);

  const onLogoFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\/(png|jpe?g)$/.test(file.type)) {
      toast.error("Please upload a PNG or JPG image.");
      return;
    }
    if (file.size > 1_000_000) {
      toast.error("Logo is too large (max 1 MB).");
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      setLogoBusy(true);
      try {
        await api.post("/settings/logo", { logo: reader.result });
        setLogo(reader.result);
        toast.success("Logo uploaded");
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Upload failed");
      } finally { setLogoBusy(false); }
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = async () => {
    setLogoBusy(true);
    try {
      await api.delete("/settings/logo");
      setLogo(null);
      toast.success("Logo removed");
    } finally { setLogoBusy(false); }
  };

  return (
    <div className="card-surface p-6" data-testid="logo-settings-card">
      <h3 className="text-navy font-semibold mb-1">Practice Logo</h3>
      <p className="text-xs text-navy/70 mb-4">Appears in the sidebar and the header of all exported PDF reports. PNG or JPG, max 1&nbsp;MB.</p>
      <div className="flex items-center gap-5">
        <div className="w-40 h-20 border border-dashed border-slate-200 rounded-lg flex items-center justify-center bg-navy/[0.02] overflow-hidden">
          {logo ? (
            <img src={logo} alt="Practice logo" data-testid="logo-preview" className="max-h-16 max-w-36 object-contain" />
          ) : (
            <ImageIcon size={28} className="text-navy/25" />
          )}
        </div>
        <div className="flex items-center gap-3">
          <label data-testid="logo-upload-btn"
            className={`flex items-center gap-2 border border-slate-200 text-navy px-4 py-2 rounded-lg text-sm font-medium hover:bg-navy/5 transition-colors duration-200 cursor-pointer ${logoBusy ? "opacity-60 pointer-events-none" : ""}`}>
            <ImageIcon size={18} /> {logo ? "Replace" : "Upload"}
            <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={onLogoFile} data-testid="logo-file-input" />
          </label>
          {logo && (
            <button onClick={removeLogo} disabled={logoBusy} data-testid="logo-remove-btn"
              className="flex items-center gap-2 text-red-600 px-3 py-2 rounded-lg text-sm font-medium hover:bg-red-50 transition-colors duration-200 disabled:opacity-60">
              <Trash size={18} /> Remove
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


function EntityBrandingTab() {
  const [logo, setLogo] = useState(null);
  const [orgName, setOrgName] = useState("");
  const [activeTid, setActiveTid] = useState(null);
  const [busy, setBusy] = useState(false);
  const [contact, setContact] = useState({ name: "", email: "" });
  const [savingContact, setSavingContact] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      setLogo(data.settings.entity_logo || null);
      setOrgName(data.settings.active_org_name || "");
      setActiveTid(data.settings.active_tenant_id || null);
      setContact(data.settings.client_contact || { name: "", email: "" });
    }).catch(() => {});
  }, []);

  const saveContact = async () => {
    setSavingContact(true);
    try {
      await api.put("/settings/entity-contact", contact);
      toast.success("Client contact saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save contact");
    } finally { setSavingContact(false); }
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\/(png|jpe?g)$/.test(file.type)) { toast.error("Please upload a PNG or JPG image."); return; }
    if (file.size > 1_000_000) { toast.error("Logo is too large (max 1 MB)."); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      setBusy(true);
      try {
        await api.post("/settings/entity-logo", { logo: reader.result });
        setLogo(reader.result);
        toast.success("Entity logo uploaded");
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Upload failed");
      } finally { setBusy(false); }
    };
    reader.readAsDataURL(file);
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete("/settings/entity-logo");
      setLogo(null);
      toast.success("Entity logo removed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
    <div className="card-surface p-6" data-testid="entity-logo-settings-card">
      <h3 className="text-navy font-semibold mb-1">Entity Logo{orgName ? ` — ${orgName}` : ""}</h3>
      <p className="text-xs text-navy/70 mb-4">
        Optional. Reports for this entity are <span className="font-semibold text-navy">dual-branded</span> — your practice logo alongside this entity logo, top-left of every PDF. PNG or JPG, max 1&nbsp;MB.
      </p>
      {!activeTid ? (
        <div className="inline-flex items-center gap-2 text-xs bg-warn/10 border border-warn/40 text-warn rounded-lg px-3 py-1.5" data-testid="entity-logo-no-entity">
          <Buildings size={14} weight="fill" /> Connect &amp; activate an organisation to set its logo.
        </div>
      ) : (
        <div className="flex items-center gap-5">
          <div className="w-40 h-20 border border-dashed border-slate-200 rounded-lg flex items-center justify-center bg-navy/[0.02] overflow-hidden">
            {logo ? (
              <img src={logo} alt="Entity logo" data-testid="entity-logo-preview" className="max-h-16 max-w-36 object-contain" />
            ) : (
              <ImageIcon size={28} className="text-navy/25" />
            )}
          </div>
          <div className="flex items-center gap-3">
            <label data-testid="entity-logo-upload-btn"
              className={`flex items-center gap-2 border border-slate-200 text-navy px-4 py-2 rounded-lg text-sm font-medium hover:bg-navy/5 transition-colors duration-200 cursor-pointer ${busy ? "opacity-60 pointer-events-none" : ""}`}>
              <ImageIcon size={18} /> {logo ? "Replace" : "Upload"}
              <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={onFile} data-testid="entity-logo-file-input" />
            </label>
            {logo && (
              <button onClick={remove} disabled={busy} data-testid="entity-logo-remove-btn"
                className="flex items-center gap-2 text-red-600 px-3 py-2 rounded-lg text-sm font-medium hover:bg-red-50 transition-colors duration-200 disabled:opacity-60">
                <Trash size={18} /> Remove
              </button>
            )}
          </div>
        </div>
      )}
    </div>

    {activeTid && (
      <div className="card-surface p-6" data-testid="client-contact-card">
        <h3 className="text-navy font-semibold mb-1">Client Contact{orgName ? ` — ${orgName}` : ""}</h3>
        <p className="text-xs text-navy/70 mb-4">
          Used as the default recipient when you email this entity's reports (Register, Forecast, Reconciliation, Balance).
        </p>
        <div className="grid sm:grid-cols-2 gap-3 max-w-2xl">
          <div>
            <label className="text-xs font-medium text-navy/70 mb-1 block">Contact name</label>
            <input value={contact.name || ""} onChange={(e) => setContact((c) => ({ ...c, name: e.target.value }))}
              placeholder="e.g. Jane Smith" data-testid="client-contact-name"
              className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-navy focus:border-orange focus:ring-2 focus:ring-orange/20 outline-none" />
          </div>
          <div>
            <label className="text-xs font-medium text-navy/70 mb-1 block">Contact email</label>
            <input type="email" value={contact.email || ""} onChange={(e) => setContact((c) => ({ ...c, email: e.target.value }))}
              placeholder="name@client.co.uk" data-testid="client-contact-email"
              className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-navy focus:border-orange focus:ring-2 focus:ring-orange/20 outline-none" />
          </div>
        </div>
        <button onClick={saveContact} disabled={savingContact} data-testid="save-client-contact-btn"
          className="mt-4 flex items-center gap-2 bg-orange text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-navy transition-colors duration-200 disabled:opacity-60">
          <FloppyDisk size={18} /> {savingContact ? "SAVING…" : "Save contact"}
        </button>
      </div>
    )}
    </div>
  );
}


const PRACTICE_FIELDS = [
  { key: "name", label: "Practice name", placeholder: "Cloud Accounting Support Services" },
  { key: "address", label: "Address", placeholder: "1 High Street, London, EC1A 1AA" },
  { key: "phone", label: "Phone", placeholder: "020 1234 5678" },
  { key: "email", label: "Email", placeholder: "hello@practice.co" },
  { key: "website", label: "Website", placeholder: "www.practice.co" },
];

export function PracticeTab() {
  const [form, setForm] = useState({ name: "", address: "", phone: "", email: "", website: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      const p = data.settings.practice || {};
      setForm({
        name: p.name || "", address: p.address || "", phone: p.phone || "",
        email: p.email || "", website: p.website || "",
      });
    }).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings/practice", form);
      toast.success("Practice details saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  const preview = PRACTICE_FIELDS.map((f) => form[f.key].trim()).filter(Boolean).join("  •  ");

  return (
    <div className="card-surface p-6 max-w-2xl" data-testid="practice-details-card">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="text-navy font-semibold mb-1">Practice Details</h3>
          <p className="text-xs text-navy/70">Shown in the footer of every exported PDF report.</p>
        </div>
        <button onClick={save} disabled={busy} data-testid="save-practice-btn"
          className="flex items-center gap-2 bg-orange text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-navy transition-colors duration-200 disabled:opacity-60">
          <FloppyDisk size={18} /> {busy ? "SAVING…" : "Save"}
        </button>
      </div>
      <div className="space-y-4">
        {PRACTICE_FIELDS.map((f) => (
          <label key={f.key} className="block">
            <span className="overline text-navy/70 block mb-1.5">{f.label}</span>
            <input value={form[f.key]} data-testid={`practice-${f.key}`} placeholder={f.placeholder}
              onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
              className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-navy placeholder:text-navy/30 focus:border-orange focus:ring-2 focus:ring-orange/25 outline-none transition-colors" />
          </label>
        ))}
      </div>
      <div className="mt-5 pt-4 border-t border-slate-100">
        <span className="overline text-navy/65 block mb-1.5">Footer preview</span>
        <div className="text-xs text-navy/70 font-mono" data-testid="practice-preview">{preview || "—"}</div>
      </div>
    </div>
  );
}
